/**
 * TakeoffDrawer - Create/Edit takeoff drawer
 * BuilderBel Construction Project Management System
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input, TextArea, Dropdown, NumberInput } from '../forms';
import type { Takeoff, CreateTakeoffRequest, ScaleUnit } from '../../types/takeoff';

interface TakeoffDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTakeoffRequest) => void;
  isSubmitting?: boolean;
  editingTakeoff?: Takeoff | null;
}

const schema = yup.object({
  name: yup.string().required('Name is required').max(200),
  description: yup.string().max(2000),
  scale: yup.number().min(0.001).max(1000),
  scaleUnit: yup.string().oneOf(['inch', 'foot', 'meter', 'centimeter']),
  totalPages: yup.number().min(1).max(1000).integer(),
});

const scaleUnitOptions = [
  { value: 'inch', label: 'Inches' },
  { value: 'foot', label: 'Feet' },
  { value: 'meter', label: 'Meters' },
  { value: 'centimeter', label: 'Centimeters' },
];

const TakeoffDrawer: React.FC<TakeoffDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  editingTakeoff,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateTakeoffRequest>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: '',
      description: '',
      scale: 1,
      scaleUnit: 'foot' as ScaleUnit,
      totalPages: 1,
    },
  });

  useEffect(() => {
    if (editingTakeoff) {
      reset({
        name: editingTakeoff.name,
        description: editingTakeoff.description || '',
        scale: editingTakeoff.scale,
        scaleUnit: editingTakeoff.scaleUnit,
        totalPages: editingTakeoff.totalPages,
      });
    } else {
      reset({
        name: '',
        description: '',
        scale: 1,
        scaleUnit: 'foot',
        totalPages: 1,
      });
    }
  }, [editingTakeoff, reset]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingTakeoff ? 'Edit Takeoff' : 'New Takeoff'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-4">
              <div>
                <Input
                  label="Name"
                  placeholder="Enter takeoff name"
                  error={errors.name?.message}
                  {...register('name')}
                />
              </div>

              <div>
                <TextArea
                  label="Description"
                  placeholder="Optional description"
                  value={watch('description') || ''}
                  onChange={val => setValue('description', val)}
                  error={errors.description?.message}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <NumberInput
                    label="Scale"
                    placeholder="1"
                    value={watch('scale') || 1}
                    onChange={val => setValue('scale', val)}
                    error={errors.scale?.message}
                  />
                </div>

                <div>
                  <Dropdown
                    label="Scale Unit"
                    options={scaleUnitOptions}
                    value={watch('scaleUnit') || 'foot'}
                    onChange={val => setValue('scaleUnit', val as ScaleUnit)}
                    error={errors.scaleUnit?.message}
                  />
                </div>
              </div>

              <div>
                <NumberInput
                  label="Total Pages"
                  placeholder="1"
                  value={watch('totalPages') || 1}
                  onChange={val => setValue('totalPages', val)}
                  error={errors.totalPages?.message}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="border-t px-8 py-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:bg-brand-200 transition-colors"
              >
                {isSubmitting
                  ? 'Saving...'
                  : editingTakeoff
                    ? 'Update Takeoff'
                    : 'Create Takeoff'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
};

export default TakeoffDrawer;
